import { DeliveredDutyPaidExtra } from '../../components/ExtrasHelper';
import { SmartSendProvider } from '~/lib/consts/providers';
import { DropLocation, QuoteApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { SmartSendItemStateBase, SmartSendKeyedState } from '../context/SmartSendContextModels';
import { chunk } from './ArrayHelper';
import GetDeliveredDutyShippingCost from './GetDeliveredDutyShippingCost';
import { NoQuoteValidation, PostQuoteValidation } from './SmartSendValidation';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useCallback } from 'react';
import { PickupShopId } from '../consts/ItemMetaConstants';
import { ItemPickupLocationCache } from './ItemPickupLocationCache';
import { isDeliveryToShopQuote } from './IsDeliveryToShopQuote';
import { ItemExtrasPostQuoteProtectionCorrector } from './ItemExtrasPostQuoteProtectionCorrector';

export const PickupLocationProximityThresholdMeters = 1609.34; // 1 Mile

type PickupLocationsValidationResult = {
  errors: string[];
  location: DropLocation | null;
};

const useQuoteHelper = () => {
  const { getBulkProhib, getDropLocations, getDropshop } = useSmartSendApi();
  const { getBulkQuoteWithValidation, getDeliveredDutyPaidCalculation } = useSmartSendApi();

  const validatePickupLocations = useCallback(
    async (q: QuoteApiModel, i: SmartSendItemApiModel): Promise<PickupLocationsValidationResult> => {
      // If not shop delivery, no pickup location needed
      if (!isDeliveryToShopQuote(q)) {
        return {
          errors: [],
          location: null
        };
      }

      // Try and get the preferred shop first
      try {
        const metaShopId = i.Meta?.[PickupShopId]?.Value;

        if (metaShopId) {
          // Cached
          const metaCachedKey = `${i.Id}-${metaShopId}`;
          if (ItemPickupLocationCache[metaCachedKey]) {
            return {
              errors: [],
              location: ItemPickupLocationCache[metaCachedKey]
            };
          }

          const res = await getDropshop(
            i.Service.Provider,
            i.Service.AccountId,
            q.Service.PickUpProviderCode,
            metaShopId
          );

          if (res.success && res.result.DropLocation?.AcceptsDelivery) {
            ItemPickupLocationCache[metaCachedKey] = res.result.DropLocation;
            return {
              errors: [],
              location: res.result.DropLocation
            };
          }
        }
      } catch (e) {
        // Ignore errors here and try to get all shops
        console.error('Error getting preferred dropshop', e);
      }

      // Get all shops and find the closest viable one
      try {
        const shops = await getDropLocations(
          i.Service.Provider,
          i.Service.AccountId,
          q.Service.PickUpProviderCode,
          i.Recipient.CountryIsoCode,
          i.Recipient.Postcode,
          'AcceptsDelivery'
        );

        const viableShops = shops.success
          ? shops.result.DropLocations.filter(
              (loc) => loc.AcceptsDelivery && loc.Distance <= PickupLocationProximityThresholdMeters
            )
          : [];

        if (viableShops.length > 0) {
          const shopToReturn = viableShops[0];
          ItemPickupLocationCache[`${i.Id}-${shopToReturn.Id}`] = shopToReturn;
          return {
            errors: [],
            location: shopToReturn
          };
        }
      } catch (e) {
        console.error('Error getting dropshop locations', e);
      }

      return {
        errors: ['SERVICE-NO-PICKUP-SHOP-AVAILABLE'],
        location: null
      };
    },
    [getDropLocations, getDropshop]
  );

  const quoteHelper = useCallback(
    async (
      items: SmartSendItemApiModel[],
      onItemSuccess: (item: SmartSendKeyedState) => void,
      onItemFail: (item: SmartSendKeyedState) => void,
      onBatchComplete: (items: SmartSendKeyedState[]) => void,
      onComplete: (items: SmartSendKeyedState[]) => void
    ): Promise<SmartSendKeyedState[]> => {
      let output: SmartSendKeyedState[] = [];

      const chunkPromises = chunk<SmartSendItemApiModel>(items, SettingsCache.settings.Limits.QuoteBatchSize).map(
        async (chunk) => {
          const chunkOutput: SmartSendKeyedState[] = [];
          try {
            const quotes = await getBulkQuoteWithValidation(chunk);
            const prohib = await getBulkProhib(chunk);

            const promises = chunk.map(async (item) => {
              let itemOutput: SmartSendKeyedState;

              try {
                // Get the main quote and validation
                let r = quotes.result.Quotes[item.Id];

                let val: SmartSendItemStateBase = {
                  validation: null,
                  state: null,
                  unsaved: null
                };

                // (Legacy Api Migration Fix)
                // Apply fixes caused by Api Migration (Public -> OrderApi), Requote if any fixes were applied.
                let unsaved = [];
                if (r) {
                  try {
                    const amendedResult = ItemExtrasPostQuoteProtectionCorrector(item, r.Quote);
                    if (amendedResult?.changed === true) {
                      const amendedQuoteResults = await getBulkQuoteWithValidation([amendedResult.item]);
                      if (amendedQuoteResults.success && amendedQuoteResults.result?.Quotes?.[amendedResult.item.Id]) {
                        r = amendedQuoteResults.result.Quotes[amendedResult.item.Id];
                        unsaved = amendedResult.unsaved;
                      }
                    }
                  } catch (e) {
                    console.error('Error applying post quote corrector', e);
                  }
                }

                console.log('Quote with validation result:', r);
                if (r) {
                  // Prohib
                  r.Restrictions = prohib.success
                    ? prohib.result.Validation.filter((x) => item.MarketplaceOrderLines.map((m) => m.Id).includes(x.Id))
                    : [];

                  const postQuoteValidation = PostQuoteValidation(item, {
                    QuoteState: r,
                    Errors: [],
                    Warnings: [],
                    Validated: false
                  });

                  // Get closest pickup locations or error if none nearby
                  if (postQuoteValidation.Errors.length === 0) {
                    const pickupValidation = await validatePickupLocations(r.Quote, item);
                    postQuoteValidation.Errors.push(...pickupValidation.errors);

                    if (pickupValidation?.location) {
                      item.Meta[PickupShopId] = {
                        Type: 'String',
                        Value: String(pickupValidation.location.Id)
                      };
                      postQuoteValidation.QuoteState.Locations = {
                        ...postQuoteValidation.QuoteState.Locations,
                        PickupLocation: pickupValidation.location
                      };
                    } else {
                      delete item.Meta[PickupShopId];
                      if (postQuoteValidation.QuoteState.Locations) {
                        postQuoteValidation.QuoteState.Locations.PickupLocation = null;
                      } else {
                        delete postQuoteValidation.QuoteState.Locations;
                      }
                    }
                  }

                  if (postQuoteValidation.Errors.length) {
                    val = { validation: postQuoteValidation, state: 'ERROR', unsaved: [] };
                  } else {
                    val = {
                      validation: postQuoteValidation,
                      state: unsaved.length ? 'SAVE' : 'IDLE',
                      unsaved: unsaved
                    };
                  }
                } else {
                  val = {
                    validation: NoQuoteValidation(item, null),
                    state: 'ERROR',
                    unsaved: []
                  };
                }

                // Get DDP
                if (item.Extras.find((x) => x.Id === DeliveredDutyPaidExtra)) {
                  const option = val?.validation?.QuoteState?.Quote?.Options?.[0];
                  if (option) {
                    const shippingCost = GetDeliveredDutyShippingCost(option);
                    const res = await getDeliveredDutyPaidCalculation(
                      item.Service.Provider,
                      item.Service.AccountId,
                      item,
                      shippingCost
                    );
                    if (res.success && res.result) {
                      option.ManualCalculations = [
                        {
                          // Show 'Duties' and 'Customs VAT' as Duties.
                          Duties: (res.result.DutyValue ?? 0) + (res.result.VatValue ?? 0),
                          Price: res.result.AdminFee ?? 0,
                          Id: DeliveredDutyPaidExtra,
                          Name: 'Delivered Duty Paid',
                          Provider: SmartSendProvider,
                          Vat: 0,
                          Total: 0
                        }
                      ];
                    }
                  }
                }

                itemOutput = { key: item.Id, value: { ...val, order: 0 } };
                if (onItemSuccess) {
                  onItemSuccess(itemOutput);
                }

                return itemOutput;
              } catch (e) {
                itemOutput = {
                  key: item.Id,
                  value: {
                    validation: NoQuoteValidation(item, e),
                    state: 'ERROR',
                    unsaved: [],
                    order: 0
                  }
                };
                if (onItemFail) {
                  onItemFail(itemOutput);
                }
                return itemOutput;
              } finally {
                chunkOutput.push(itemOutput);
              }
            });

            await Promise.all(promises);
          } catch (e) {
            // Fail for chunk
            chunk.forEach((c) => {
              chunkOutput.push({
                key: c.Id,
                value: {
                  validation: NoQuoteValidation(c, e),
                  state: 'ERROR',
                  unsaved: [],
                  order: 0
                }
              });
            });
          } finally {
            output = output.concat(chunkOutput);
            if (onBatchComplete) {
              onBatchComplete(chunkOutput);
            }
          }
        }
      );

      await Promise.all(chunkPromises);

      if (onComplete) {
        onComplete(output);
      }

      return output;
    },
    [getBulkProhib, getBulkQuoteWithValidation, getDeliveredDutyPaidCalculation, validatePickupLocations]
  );

  return { quoteHelper } as const;
};

export default useQuoteHelper;
